import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';
import { AuthLayout } from './AuthLayout.js';

export function MfaChallengeScreen(): ReactNode {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [code, setCode] = useState('');

  const verify = useMutation({
    mutationFn: () => api.post('/api/v1/auth/mfa/verify', { code: code.trim() }),
    onSuccess: async () => {
      await refresh();
      await navigate({ to: '/' });
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    verify.mutate();
  };

  const error = verify.error;

  return (
    <AuthLayout
      title={t('auth.mfa.title')}
      subtitle={t('auth.mfa.subtitle')}
      footer={
        <p>
          <a href="/sign-in">{t('auth.backToSignIn')}</a>
        </p>
      }
    >
      <form className="form" onSubmit={onSubmit} noValidate>
        {error === null || error === undefined ? null : (
          <Alert tone="critical">
            {error instanceof ApiError ? t(`error.${error.code}`) : t('error.SERVICE_UNAVAILABLE')}
          </Alert>
        )}

        <TextField
          label={t('auth.mfaCode')}
          name="code"
          // One-time-code autocomplete lets the platform offer the code from a message or
          // password manager, which is the difference between usable and merely correct.
          autoComplete="one-time-code"
          inputMode="numeric"
          autoFocus
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />

        <Button type="submit" variant="primary" loading={verify.isPending}>
          {t('auth.mfa.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
